import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import * as asuntosApi from '@/api/asuntos.api';
import type { Asunto } from '@/api/asuntos.api';
import { HojaDelAsunto } from '@/components/athena/HojaDelAsunto';
import { NuevoAsunto } from '@/components/athena/NuevoAsunto';
import { CAJON_COLOR, hora12, fechaLarga } from '@/lib/athena';

/**
 * AGENDA — un calendario de verdad, no una lista con fechas.
 *
 * Mandato: *"Un calendario real, no una lista."* Día, Semana y Mes, el botón
 * Hoy y las flechas.
 *
 * ═══ PUNTEADO vs RELLENO ═══
 *
 * Lo DETECTADO sale punteado: todavía es una suposición de la máquina. Lo
 * APROBADO sale relleno. Dibujarlos igual sería pedirle al dueño que planifique
 * su semana sobre algo que nadie confirmó.
 *
 * ═══ LOS CHOQUES SE MARCAN ═══
 *
 * Si dos citas se pisan, las dos llevan borde rojo. Es un cálculo del panel y
 * no del servidor a propósito: depende de qué se está mirando, y el servidor no
 * sabe si el dueño está viendo el día o el mes.
 */

type Vista = 'dia' | 'semana' | 'mes';

const DIAS_CORTOS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

function inicioDeDia(d: Date, sumar = 0): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() + sumar);
  return x;
}

function inicioDeSemana(d: Date): Date {
  const x = inicioDeDia(d);
  x.setDate(x.getDate() - x.getDay());
  return x;
}

/** Qué asuntos se pisan con otro. Devuelve los ids en conflicto. */
function choques(lista: Asunto[]): Set<string> {
  const fuera = new Set<string>();
  const orden = [...lista].sort(
    (a, b) => new Date(a.fechaCompromiso).getTime() - new Date(b.fechaCompromiso).getTime(),
  );
  for (let i = 1; i < orden.length; i++) {
    const previo = new Date(orden[i - 1]!.fechaCompromiso).getTime();
    const actual = new Date(orden[i]!.fechaCompromiso).getTime();
    // Menos de 45 minutos entre dos compromisos con gente distinta: se pisan.
    if (actual - previo < 45 * 60_000 && orden[i]!.contactId !== orden[i - 1]!.contactId) {
      fuera.add(orden[i]!.id);
      fuera.add(orden[i - 1]!.id);
    }
  }
  return fuera;
}

function Bloque({
  a,
  enChoque,
  onAbrir,
}: {
  a: Asunto;
  enChoque: boolean;
  onAbrir: (id: string) => void;
}) {
  const color = CAJON_COLOR[a.cajon];
  return (
    <button
      onClick={() => onAbrir(a.id)}
      className={cn(
        'flex min-h-[44px] w-full items-start gap-2 rounded-md border px-2 py-1.5 text-left text-xs transition',
        color.chip,
        // Punteado = todavía es una suposición. Relleno = lo confirmó el dueño.
        a.esSuposicion ? 'border-dashed opacity-80' : 'border-transparent',
        enChoque && 'ring-2 ring-red-500',
      )}
    >
      <span className="shrink-0 font-medium tabular-nums">{hora12(a.fechaCompromiso)}</span>
      <span className="min-w-0 flex-1 truncate">{a.descripcion}</span>
    </button>
  );
}

export default function Agenda() {
  const [vista, setVista] = useState<Vista>('dia');
  const [ancla, setAncla] = useState(() => new Date());
  const [abierto, setAbierto] = useState<string | null>(null);
  const [anotando, setAnotando] = useState(false);

  const desde =
    vista === 'dia'
      ? inicioDeDia(ancla)
      : vista === 'semana'
        ? inicioDeSemana(ancla)
        : new Date(ancla.getFullYear(), ancla.getMonth(), 1);
  const hasta =
    vista === 'dia'
      ? inicioDeDia(ancla, 1)
      : vista === 'semana'
        ? inicioDeDia(inicioDeSemana(ancla), 7)
        : new Date(ancla.getFullYear(), ancla.getMonth() + 1, 1);

  const asuntos = useQuery({
    queryKey: ['asuntos', 'agenda', vista, desde.toISOString()],
    queryFn: () =>
      asuntosApi.getAsuntos({
        desde: desde.toISOString(),
        hasta: hasta.toISOString(),
        limite: 400,
      }),
  });

  const lista = asuntos.data?.asuntos ?? [];
  const enChoque = choques(lista);

  const mover = (dir: -1 | 1) => {
    const x = new Date(ancla);
    if (vista === 'dia') x.setDate(x.getDate() + dir);
    else if (vista === 'semana') x.setDate(x.getDate() + dir * 7);
    else x.setMonth(x.getMonth() + dir);
    setAncla(x);
  };

  const porDia = (d: Date) =>
    lista.filter((a) => {
      const f = new Date(a.fechaCompromiso);
      return (
        f.getDate() === d.getDate() &&
        f.getMonth() === d.getMonth() &&
        f.getFullYear() === d.getFullYear()
      );
    });

  const sinConfirmar = lista.filter((a) => a.esSuposicion);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold text-slate-900 first-letter:uppercase dark:text-slate-50">Agenda</h1>
        <button
          onClick={() => setAnotando(true)}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-brand-600 text-white"
          aria-label="Anotar algo"
        >
          <Plus className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-1">
          {(['dia', 'semana', 'mes'] as Vista[]).map((v) => (
            <button
              key={v}
              onClick={() => setVista(v)}
              className={cn(
                'min-h-[36px] rounded-md px-2.5 text-sm capitalize',
                v === vista
                  ? 'bg-brand-600 text-white'
                  : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
              )}
            >
              {v === 'dia' ? 'Día' : v}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => mover(-1)}
          aria-label="Anterior"
          className="grid h-11 w-11 place-items-center rounded-md bg-slate-100 dark:bg-slate-800"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          onClick={() => setAncla(new Date())}
          className="min-h-[44px] rounded-md bg-slate-100 px-3 text-sm dark:bg-slate-800"
        >
          Hoy
        </button>
        <button
          onClick={() => mover(1)}
          aria-label="Siguiente"
          className="grid h-11 w-11 place-items-center rounded-md bg-slate-100 dark:bg-slate-800"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <span className="ml-1 text-sm text-slate-600 first-letter:uppercase dark:text-slate-300">
          {vista === 'mes'
            ? ancla.toLocaleDateString('es-DO', { month: 'long', year: 'numeric' })
            : fechaLarga(desde)}
        </span>
      </div>

      {asuntos.isPending ? (
        <Spinner />
      ) : (
        <div className="grid gap-3 lg:grid-cols-[1fr_280px]">
          <Card>
            <CardBody className="space-y-2">
              {vista === 'dia' && (
                <>
                  {porDia(desde).length === 0 ? (
                    <p className="py-10 text-center text-sm text-slate-400">
                      Nada agendado este día.
                    </p>
                  ) : (
                    porDia(desde).map((a) => (
                      <Bloque key={a.id} a={a} enChoque={enChoque.has(a.id)} onAbrir={setAbierto} />
                    ))
                  )}
                </>
              )}

              {vista === 'semana' && (
                <div className="grid gap-2 sm:grid-cols-7">
                  {Array.from({ length: 7 }, (_, i) => inicioDeDia(desde, i)).map((d) => (
                    <div key={d.toISOString()} className="min-w-0">
                      <p className="mb-1 text-xs font-semibold capitalize text-slate-500">
                        {DIAS_CORTOS[d.getDay()]} {d.getDate()}
                      </p>
                      <div className="space-y-1">
                        {porDia(d).map((a) => (
                          <Bloque
                            key={a.id}
                            a={a}
                            enChoque={enChoque.has(a.id)}
                            onAbrir={setAbierto}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {vista === 'mes' && (
                <div className="grid grid-cols-7 gap-1">
                  {DIAS_CORTOS.map((d) => (
                    <p key={d} className="pb-1 text-center text-xs text-slate-400">
                      {d}
                    </p>
                  ))}
                  {/* Los huecos hasta el primer día del mes. */}
                  {Array.from({ length: desde.getDay() }, (_, i) => (
                    <div key={`hueco-${i}`} />
                  ))}
                  {Array.from(
                    { length: new Date(ancla.getFullYear(), ancla.getMonth() + 1, 0).getDate() },
                    (_, i) => new Date(ancla.getFullYear(), ancla.getMonth(), i + 1),
                  ).map((d) => {
                    const delDia = porDia(d);
                    return (
                      <button
                        key={d.toISOString()}
                        onClick={() => {
                          setAncla(d);
                          setVista('dia');
                        }}
                        className="min-h-[44px] rounded border border-slate-100 p-1 text-left text-xs dark:border-slate-800"
                      >
                        <span className="block text-slate-600 dark:text-slate-300">
                          {d.getDate()}
                        </span>
                        <span className="mt-0.5 flex flex-wrap gap-0.5">
                          {delDia.slice(0, 4).map((a) => (
                            <span
                              key={a.id}
                              className={cn('h-1.5 w-1.5 rounded-full', CAJON_COLOR[a.cajon].punto)}
                            />
                          ))}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Columna derecha en escritorio: lo que falta confirmar. */}
          <Card className="hidden lg:block">
            <CardBody className="space-y-2">
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Por confirmar ({sinConfirmar.length})
              </p>
              {sinConfirmar.length === 0 ? (
                <p className="text-sm text-slate-400">Todo confirmado.</p>
              ) : (
                sinConfirmar.map((a) => (
                  <Bloque key={a.id} a={a} enChoque={false} onAbrir={setAbierto} />
                ))
              )}
            </CardBody>
          </Card>
        </div>
      )}

      <NuevoAsunto abierto={anotando} onCerrar={() => setAnotando(false)} />
      <HojaDelAsunto asuntoId={abierto} onCerrar={() => setAbierto(null)} />
    </div>
  );
}
